import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../../components/PageConfigProvider/constants';
import type { TabContent } from '../../components/PageConfigProvider/page-reducer';
import {
  type Page,
  PageType,
  RevelioPageId,
} from '../../components/PageConfigProvider/types';
import { FilterId } from '../../filters/types/filters';

const hourlyItems: TabContent['items'] = [
  {
    position: { x: 0, width: 16, y: 0, height: 9 },
    id: 'industry-pay-hourly-compensation',
    title: 'Hourly Compensation',
    info: {
      text: 'The average predicted hourly pay over time. More information on the compensation model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#salary',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['contractor_pay_rate'],
      chartType: 'line',
    },
  },
  {
    position: { x: 16, width: 16, y: 0, height: 9 },
    id: 'industry-pay-hourly-time-to-fill',
    title: 'Time to Fill',
    info: {
      text: 'The average number of days that job postings are active before being removed.',
    },
    plot: {
      defaultMetricOptions: ['time_to_fill'],
      chartType: 'line',
    },
  },
  {
    position: { x: 32, width: 16, y: 0, height: 18 },
    id: 'industry-pay-hourly-top-roles',
    title: 'Top Roles',
    info: {
      text: 'The most common roles within this industry. More information on the occupational taxonomy can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#jobs-taxonomy',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['contractor_pay_rate'],
      chartType: 'box',
      secondaryDimension: 'role',
    },
  },
  {
    position: { x: 32, width: 16, y: 18, height: 18 },
    id: 'industry-pay-hourly-top-geographies',
    title: 'Top Geographies',
    info: {
      text: 'The most common geographies within this industry.',
    },
    plot: {
      defaultMetricOptions: ['contractor_pay_rate'],
      secondaryDimension: 'geography',
      chartType: 'box',
    },
  },
  {
    position: { x: 0, width: 32, y: 9, height: 27 },
    id: 'industry-pay-hourly-distribution',
    title: 'Distribution',
    info: {
      text: 'The distribution of hourly pay. More information on the compensation model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#salary',
        },
      ],
    },
    plot: {
      chartType: 'distribution',
      defaultMetricOptions: ['contractor_pay_rate'],
      distributionCutoffLower: 1,
      distributionCutoffUpper: 99,
    },
  },
];

const baseItems: TabContent['items'] = [
  {
    position: { x: 0, width: 16, y: 0, height: 9 },
    id: 'industry-pay-base-compensation',
    title: 'Base Compensation',
    info: {
      text: 'The average predicted base salary over time. More information on the compensation model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#salary',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['base_salary'],
      chartType: 'line',
    },
  },
  {
    position: { x: 16, width: 16, y: 0, height: 9 },
    id: 'industry-pay-base-time-to-fill',
    title: 'Time to Fill',
    info: {
      text: 'The average number of days that job postings are active before being removed.',
    },
    plot: {
      defaultMetricOptions: ['time_to_fill'],
      chartType: 'line',
    },
  },
  {
    position: { x: 32, width: 16, y: 0, height: 18 },
    id: 'industry-pay-base-top-roles',
    title: 'Top Roles',
    info: {
      text: 'The most common roles within this industry. More information on the occupational taxonomy can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#jobs-taxonomy',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['base_salary'],
      chartType: 'box',
      secondaryDimension: 'role',
    },
  },
  {
    position: { x: 32, width: 16, y: 18, height: 18 },
    id: 'industry-pay-base-top-geographies',
    title: 'Top Geographies',
    info: {
      text: 'The most common geographies within this industry.',
    },
    plot: {
      defaultMetricOptions: ['base_salary'],
      secondaryDimension: 'geography',
      chartType: 'box',
    },
  },
  {
    position: { x: 0, width: 32, y: 9, height: 27 },
    id: 'industry-pay-base-distribution',
    title: 'Distribution',
    info: {
      text: 'The distribution of base salary. More information on the compensation model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#salary',
        },
      ],
    },
    plot: {
      chartType: 'distribution',
      defaultMetricOptions: ['base_salary'],
      distributionCutoffLower: 1,
      distributionCutoffUpper: 99,
    },
  },
];

const totalItems: TabContent['items'] = [
  {
    position: { x: 0, width: 16, y: 0, height: 9 },
    id: 'industry-pay-total-compensation',
    title: 'Total Compensation',
    info: {
      text: 'The average predicted total compensation over time. More information on the compensation model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#salary',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['total_compensation'],
      chartType: 'line',
    },
  },
  {
    position: { x: 16, width: 16, y: 0, height: 9 },
    id: 'industry-pay-total-time-to-fill',
    title: 'Time to Fill',
    info: {
      text: 'The average number of days that job postings are active before being removed.',
    },
    plot: {
      defaultMetricOptions: ['time_to_fill'],
      chartType: 'line',
    },
  },
  {
    position: { x: 32, width: 16, y: 0, height: 18 },
    id: 'industry-pay-total-top-roles',
    title: 'Top Roles',
    info: {
      text: 'The most common roles within this industry. More information on the occupational taxonomy can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#jobs-taxonomy',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['total_compensation'],
      chartType: 'box',
      secondaryDimension: 'role',
    },
  },
  {
    position: { x: 32, width: 16, y: 18, height: 18 },
    id: 'industry-pay-total-top-geographies',
    title: 'Top Geographies',
    info: {
      text: 'The most common geographies within this industry.',
    },
    plot: {
      defaultMetricOptions: ['total_compensation'],
      secondaryDimension: 'geography',
      chartType: 'box',
    },
  },
  {
    position: { x: 0, width: 32, y: 9, height: 27 },
    id: 'industry-pay-total-distribution',
    title: 'Distribution',
    info: {
      text: 'The distribution of total compensation. More information on the compensation model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#salary',
        },
      ],
    },
    plot: {
      chartType: 'distribution',
      defaultMetricOptions: ['total_compensation'],
      distributionCutoffLower: 1,
      distributionCutoffUpper: 99,
    },
  },
];

const sharedConfig: Pick<
  TabContent,
  'secondaryFilters' | 'grid' | 'primaryFilter'
> = {
  secondaryFilters: [
    FilterId.ACTIVITY,
    FilterId.CONTINGENT_WORKERS,
    FilterId.EDUCATION,
    FilterId.ETHNICITY,
    FilterId.GENDER,
    FilterId.GEOGRAPHY,
    FilterId.ROLE,
    FilterId.SENIORITY,
    FilterId.CUSTOM_SKILL,
    FilterId.DATE_RANGE,
    FilterId.KEYWORDS,
    FilterId.DATA_ADJUSTMENTS,
    FilterId.TITLE,
  ].map((id) => ({ id })),
  grid: { rows: DEFAULT_GRID_ROWS, columns: DEFAULT_GRID_COLUMNS },
  primaryFilter: {
    id: FilterId.INDUSTRY,
    tagColors: true,
  },
};
const industryPayHourlyTab: TabContent = {
  id: 'industry-pay-hourly',
  items: hourlyItems,
  ...sharedConfig,
};

const industryPayBaseTab: TabContent = {
  id: 'industry-pay-base',
  items: baseItems,
  ...sharedConfig,
};

const industryPayTotalTab: TabContent = {
  id: 'industry-pay-total',
  items: totalItems,
  ...sharedConfig,
};

export const industryPay: Page = {
  type: PageType.REVELIO_PUBLIC,
  id: RevelioPageId.INDUSTRY_PAY,
  revelioPageId: RevelioPageId.INDUSTRY_PAY,
  name: 'Compensation',
  owner: { id: 'revelio-labs', username: 'revelio-labs' },
  tabs: [
    {
      id: 'industry-pay-hourly',
      label: 'Hourly',
      tabContent: industryPayHourlyTab,
      isActive: false,
    },
    {
      id: 'industry-pay-base',
      label: 'Base',
      tabContent: industryPayBaseTab,
      isActive: false,
    },
    {
      id: 'industry-pay-total',
      label: 'Total',
      tabContent: industryPayTotalTab,
      isActive: true,
    },
  ],
};
